import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddressContext = createContext(null);

export const useAddress = () => {
  const context = useContext(AddressContext);
  if (!context) {
    throw new Error('useAddress must be used within an AddressProvider');
  }
  return context;
};

export const AddressProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [loading, setLoading] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchAddresses = useCallback(async () => {
    if (!isAuthenticated) {
      setAddresses([]);
      setSelectedAddress(null);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/customer/addresses`, getAuthHeaders());
      const addressList = response.data.addresses || [];
      setAddresses(addressList);
      
      // Set default address as selected
      const defaultAddr = addressList.find(a => a.is_default);
      if (defaultAddr) {
        setSelectedAddress(defaultAddr);
      } else if (addressList.length > 0) {
        setSelectedAddress(addressList[0]);
      }
    } catch (error) {
      console.error('Error fetching addresses:', error);
      setAddresses([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  const addAddress = async (addressData) => {
    try {
      const response = await axios.post(`${API}/customer/addresses`, addressData, getAuthHeaders());
      await fetchAddresses();
      return response.data;
    } catch (error) {
      console.error('Error adding address:', error);
      throw error;
    }
  };

  const updateAddress = async (addressId, addressData) => {
    try {
      const response = await axios.put(`${API}/customer/addresses/${addressId}`, addressData, getAuthHeaders());
      await fetchAddresses();
      return response.data;
    } catch (error) {
      console.error('Error updating address:', error);
      throw error;
    }
  };

  const deleteAddress = async (addressId) => {
    try {
      await axios.delete(`${API}/customer/addresses/${addressId}`, getAuthHeaders());
      await fetchAddresses();
    } catch (error) {
      console.error('Error deleting address:', error);
      throw error;
    }
  };

  const setDefaultAddress = async (addressId) => {
    try {
      await axios.put(`${API}/customer/addresses/${addressId}/set-default`, {}, getAuthHeaders());
      await fetchAddresses();
    } catch (error) {
      console.error('Error setting default address:', error);
      throw error;
    }
  };

  const selectAddress = (address) => {
    setSelectedAddress(address);
  };

  return (
    <AddressContext.Provider value={{
      addresses,
      selectedAddress,
      loading,
      addAddress,
      updateAddress,
      deleteAddress,
      setDefaultAddress,
      selectAddress,
      fetchAddresses
    }}>
      {children}
    </AddressContext.Provider>
  );
};
